import type { CellContext, ColumnDef } from '@tanstack/react-table';
import type { ZodError } from 'zod';

import { userMassImportSchema } from '@repo/schemas/user/userMassImport';
import { format, isValid } from 'date-fns';
import { Check, ChevronLeft, HelpCircle, Import } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { v4 as uuidv4 } from 'uuid';

import UserMassImport from '@/components/features/manage/manage-users/UserMassImport';
import UserMassImportContent from '@/components/features/manage/manage-users/UserMassImportContent';
import { UserMassImportError } from '@/components/features/manage/manage-users/UserMassImportError';
import UserMassImportInfo from '@/components/features/manage/manage-users/UserMassImportInfo';
import TanstackTableDynamicCell from '@/components/features/manage/tanstack-table-dynamic/TanstackTableDynamicCell';
import { TanstackTableDynamicEditCell } from '@/components/features/manage/tanstack-table-dynamic/TanstackTableDynamicEditCell';
import TanstackTableDynamic from '@/components/features/manage/tanstack-table-dynamic/TanstackTableDynamicProvider';
import { Badge } from '@/components/ui/badge';
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from '@/components/ui/breadcrumb';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';

export interface UsersMassImportRow {
  id: string;
  lvl: string;
  name: string;
  surname: string;
  email: string;
  phone: string | null;
  birthdate: string | null;
}

export interface ErrorsT {
  errors: ZodError['issues'];
  data: UsersMassImportRow;
  index: number;
}

export default function ManageUsersMassImportPage() {
  const navigate = useNavigate();

  const [openSubmitDialog, setOpenSubmitDialog] = useState<boolean>(false);
  const [openErrorDialog, setOpenErrorDialog] = useState<boolean>(false);
  const [openImportDialog, setOpenImportDialog] = useState<boolean>(false);
  const [openInfoDialog, setOpenInfoDialog] = useState<boolean>(false);

  const [userList, setUserList] = useState<UsersMassImportRow[]>([]);
  const [errors, setErrors] = useState<ErrorsT[]>([]);

  const handleOpenImportDialog = () => {
    setOpenImportDialog(true);
  };

  const handleOpenInfoDialog = () => {
    setOpenInfoDialog(true);
  };
  const handleSaveChanges = () => {
    const isError = checkErrors();
    if (isError) {
      setOpenErrorDialog(true);
    } else {
      setOpenSubmitDialog(true);
    }
  };

  const checkErrors = () => {
    const errorsArray: ErrorsT[] = [];
    userList.forEach((user, index) => {
      const { error } = userMassImportSchema.safeParse(user);
      if (error) {
        errorsArray.push({ errors: error.issues, data: user, index: index });
      }
    });
    setErrors(errorsArray);
    return errorsArray.length !== 0;
  };

  const columns: ColumnDef<UsersMassImportRow>[] = useMemo(
    () => [
      {
        id: 'lvl',
        accessorKey: 'lvl',
        cell: (props: CellContext<UsersMassImportRow, unknown>) => (
          <TanstackTableDynamicCell
            {...props}
            placeholder={'Stanowisko'}
            type={'select'}
            options={[
              { value: '0', label: 'Uczeń' },
              { value: '1', label: 'Nauczyciel' },
              { value: '2', label: 'Administrator' },
            ]}
            viewCell={() => {
              const value = props.getValue();

              if (value === '1') {
                return <Badge variant={'turquoise'}>Nauczyciel</Badge>;
              }
              if (value === '2') {
                return <Badge variant={'purple'}>Administrator</Badge>;
              }

              return <Badge variant={'blue'}>Uczeń</Badge>;
            }}
          />
        ),
        header: 'Rola',
      },
      {
        id: 'name',
        accessorKey: 'name',
        cell: (props: CellContext<UsersMassImportRow, unknown>) => <TanstackTableDynamicCell {...props} type={'text'} placeholder={'Imię'} />,
        header: 'Imię',
      },
      {
        id: 'surname',
        accessorKey: 'surname',
        cell: (props: CellContext<UsersMassImportRow, unknown>) => <TanstackTableDynamicCell {...props} placeholder={'Nazwisko'} type={'text'} />,
        header: 'Nazwisko',
      },
      {
        id: 'email',
        accessorKey: 'email',
        cell: (props: CellContext<UsersMassImportRow, unknown>) => <TanstackTableDynamicCell {...props} placeholder={'Email'} type={'email'} />,
        header: 'Email',
      },
      {
        id: 'phone',
        accessorKey: 'phone',
        cell: (props: CellContext<UsersMassImportRow, unknown>) => <TanstackTableDynamicCell {...props} placeholder={'Nr. tel'} type={'phone'} />,
        header: 'Nr.tel',
      },
      {
        id: 'birthdate',
        accessorKey: 'birthdate',
        cell: (props: CellContext<UsersMassImportRow, unknown>) => (
          <TanstackTableDynamicCell
            {...props}
            type={'date'}
            viewCell={(value) => {
              if (typeof value === 'string' && isValid(new Date(value))) {
                return format(new Date(value), 'dd/MM/yyyy');
              }
              if (value === undefined || value === null || value === '') {
                return '';
              }
              return 'Niepoprawny format daty';
            }}
          />
        ),
        header: 'Data ur.',
      },
      {
        id: 'actions',
        enableHiding: false,
        enableSorting: false,
        cell: ({ row }) => <TanstackTableDynamicEditCell row={row} />,
      },
    ],
    []
  );

  return (
    <>
      <UserMassImportContent open={openImportDialog} onOpenChange={setOpenImportDialog} setData={setUserList} />
      <UserMassImport data={userList} open={openSubmitDialog} onOpenChange={setOpenSubmitDialog} />
      <UserMassImportError errors={errors} open={openErrorDialog} onOpenChange={setOpenErrorDialog} />
      <UserMassImportInfo open={openInfoDialog} onOpenChange={setOpenInfoDialog} />
      <div className={'base-direction-col h-full'}>
        <div className={'viewport-height flex w-full flex-col gap-4'}>
          <header className={'bg-background border-border-primary rounded-2xl border'}>
            <div className={'flex w-full items-center justify-between gap-3 p-4'}>
              <div className={'flex items-center gap-1'}>
                <Button size={'sm'} variant={'outline'} onClick={() => void navigate('/manage/users')}>
                  <ChevronLeft />
                </Button>
                <Breadcrumb>
                  <BreadcrumbList>
                    <BreadcrumbItem>
                      <BreadcrumbLink asChild>
                        <Link to={'/manage/users'}>Użytkownicy</Link>
                      </BreadcrumbLink>
                    </BreadcrumbItem>
                    <BreadcrumbSeparator />
                    <BreadcrumbItem>
                      <BreadcrumbPage>Masowy import</BreadcrumbPage>
                    </BreadcrumbItem>
                  </BreadcrumbList>
                </Breadcrumb>
              </div>
              <Button onClick={handleSaveChanges} disabled={!userList.length}>
                <Check />
                Zapisz
              </Button>
            </div>
            <Separator />
            <div className={'flex w-full justify-between gap-3 p-4'}>
              <Button variant={'outline'} className={'w-fit'} onClick={handleOpenImportDialog}>
                <Import />
                Importuj dane
              </Button>
              <Button variant={'outline'} onClick={handleOpenInfoDialog}>
                <HelpCircle />
              </Button>
            </div>
          </header>

          <TanstackTableDynamic
            data={userList}
            setData={setUserList}
            columns={columns}
            newRowTemplate={{
              id: uuidv4(),
              lvl: '0',
              name: '',
              surname: '',
              email: '',
              phone: null,
              birthdate: null,
            }}
          />
        </div>
      </div>
    </>
  );
}
